import type { Collection } from '@prisma/client'
import type { SerializeFrom } from '@remix-run/node'
import { useFetcher, useSearchParams } from '@remix-run/react'
import { CheckIcon } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { CartShipping } from '~/components/cart/cart-shipping'
import { Price } from '~/components/price'
import { ProductMediaGallery } from '~/components/product/product-media-gallery'
import { ProductPageLayout as Layout } from '~/components/product/product-page-layout'
import { ShopLink } from '~/components/shop-link'
import { makeShopOwnPath } from '~/helpers/routes'
import { getEntireStoreDiscount } from '~/helpers/shop'
import { useOptionalCart } from '~/hooks/data/use-cart'
import { useShop } from '~/hooks/data/use-shop'
import type { ProductFull } from '~/models/product.server'
import type { ShippingCountryCode } from '~/schemas/country'
import { Badge, Button, Tag } from '~/ui'
import { ToggleGroup } from '~/ui/toggle-group'
import { cn, cva } from '~/utils/classnames'
import { isTodayOrTomorrow, parsePresaleDate } from '~/utils/date'

const labelStyle = cva({
  base: 'text-foreground/64 mb-3 text-xs',
})

interface ProductPageProps {
  product: SerializeFrom<ProductFull>
  buyerCountry?: ShippingCountryCode
  collections: Pick<
    SerializeFrom<Collection>,
    'id' | 'slug' | 'name' | 'disposition' | 'archivedAt'
  >[]
}

export const ProductPage = ({
  product,
  buyerCountry,
  collections = [],
}: ProductPageProps) => {
  const {
    name,
    descriptionPrimary,
    isPreview,
    isPresale,
    descriptionSecondary,
  } = product
  const format = product.mediaFormat || 'square'

  const { t } = useTranslation()
  const shop = useShop()

  const fetcher = useFetcher()
  const cart = useOptionalCart()
  const variants = useProductVariants(product)

  const [searchParams, setSearchParams] = useSearchParams()
  const variantParam = searchParams.get('variant')

  const selectedVariant = useMemo(() => {
    return (
      variants.find((variant) => variantParam === variant.name) ||
      variants.find((variant) => variant.isDefault) ||
      variants[0]!
    )
  }, [variantParam, variants])

  const selectedVariantQuantityInCart = useMemo(() => {
    if (!cart) return 0
    return (
      cart.cartLines.find(
        ({ productVariant }) => productVariant.id === selectedVariant.id
      )?.quantity || 0
    )
  }, [cart, selectedVariant.id])

  const isSingleVariant = useMemo(() => variants.length === 1, [variants])

  const addToCartLabel = useMemo(() => {
    if (isPreview) {
      return t('noun.coming_soon')
    }

    if (selectedVariant.isOutOfStock) {
      return t('components.inventory.out_of_stock')
    }

    if (selectedVariantQuantityInCart) {
      return t('page.products.view_product.added_to_cart')
    }

    return t('page.products.view_product.add_to_cart')
  }, [
    isPreview,
    t,
    selectedVariant.isOutOfStock,
    selectedVariantQuantityInCart,
  ])

  const price = useMemo(() => {
    const discount = getEntireStoreDiscount(shop) || 0

    const previous = selectedVariant.price
    const current =
      selectedVariant.price - (selectedVariant.price * discount) / 100

    return {
      current,
      previous: previous !== current ? previous : null,
    }
  }, [selectedVariant.price, shop])

  const handleVariantSelection = (variant: (typeof variants)[number]) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('variant', variant.name)
        return next
      },
      { replace: true, preventScrollReset: true }
    )
  }

  const handleAddToCart = () => {
    fetcher.submit(
      {
        productVariantId: selectedVariant.id,
        quantity: selectedVariantQuantityInCart + 1,
      },
      { method: 'post', action: makeShopOwnPath(shop, '/cart') }
    )
  }

  return (
    <Layout>
      <Layout.Media format={format}>
        <ProductMediaGallery
          attachments={selectedVariant.attachments}
          format={format}
        />
      </Layout.Media>
      <Layout.Content className="flex flex-col p-0 @max-lg:overflow-visible @lg:overflow-y-auto">
        <section className="space-y-2 p-5">
          <div className="flex items-center gap-1">
            {isPreview && <Badge>{t('noun.coming_soon')}</Badge>}
            {isPresale && <Badge>{t('noun.pre_sale')}</Badge>}
            {isPresale && (
              <p className="text-foreground/64 text-xs font-medium">
                {t(
                  isTodayOrTomorrow(product.publishedAt!)
                    ? 'noun.pre_sale_date'
                    : 'noun.pre_sale_date_on',
                  {
                    date: parsePresaleDate(product.publishedAt!).date,
                    time: parsePresaleDate(product.publishedAt!).time,
                  }
                )}
              </p>
            )}
          </div>
          <h1 className="text-2xl font-medium">{name}</h1>
          {descriptionPrimary && (
            <p className="text-foreground/64 text-lg leading-tight">
              {descriptionPrimary}
            </p>
          )}
          {collections.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {collections.map((collection) => (
                <Tag
                  key={collection.id}
                  className="text-xs"
                  interactive
                  asChild
                >
                  <ShopLink
                    to={`/collections/${collection.id}/${collection.slug}`}
                  >
                    {collection.name}
                  </ShopLink>
                </Tag>
              ))}
            </div>
          )}
        </section>

        {descriptionSecondary?.trim() && (
          <section className="border-border/4 flex flex-col gap-1 border-t p-5">
            <p className="text-sm font-medium">
              {t('noun.additional_information')}
            </p>
            <p className="text-foreground/64 text-sm">{descriptionSecondary}</p>
          </section>
        )}

        {!isSingleVariant && (
          <section className="border-border/4 flex flex-col gap-2 border-t p-5">
            <ToggleGroup
              name="variant"
              value={selectedVariant}
              onValueChange={(variant) => handleVariantSelection(variant)}
              compare={(a, b) => a.id === b.id}
            >
              {variants.map((variant) => (
                <ToggleGroup.Item
                  key={variant.id}
                  value={variant}
                  disabled={variant.isOutOfStock}
                >
                  {variant.name}
                </ToggleGroup.Item>
              ))}
            </ToggleGroup>
          </section>
        )}

        <div className="mt-auto">
          <section
            className={cn('border-border/4 relative border-t px-5 py-3')}
          >
            <CartShipping fetcher={fetcher} buyerCountry={buyerCountry} />
          </section>
          <section className="border-border/4 border-t p-5">
            <Button
              size="lg"
              disabled={isPreview || selectedVariant.isOutOfStock}
              onClick={handleAddToCart}
              className="w-full"
            >
              {!!selectedVariantQuantityInCart && <CheckIcon />}
              {addToCartLabel}
              <div className="border-foreground/20 relative ml-auto border-l pl-2.5">
                <Price amount={price.current} />
                {price.previous && (
                  <Price amount={price.previous} variant="strikethrough" />
                )}
              </div>
            </Button>
          </section>
        </div>
      </Layout.Content>
    </Layout>
  )
}

const useProductVariants = (product: SerializeFrom<ProductFull>) => {
  return useMemo(() => {
    const variants = product.variants
      .map((variant) => {
        const trait = variant.markers[0]?.trait
        if (!trait || !variant.isVisible || !!variant.archivedAt) return null

        return {
          id: variant.id,
          name: trait.name,
          attachments: product.attachments
            .filter(
              (attachment) =>
                !attachment.archivedAt && attachment.targetId === variant.id
            )
            .sort(
              (a, b) => Number(a.disposition || 0) - Number(b.disposition || 0)
            ),
          price: variant.price,
          stock: variant.stock,
          isDefault: trait.isDefault,
          isOutOfStock: variant.stock <= 0 && product.isStrictStock,
          isSingleVariant: false,
        }
      })
      .filter((v): v is NonNullable<typeof v> => !!v)

    // if no variants with traits are found, return the first visible variant as a single variant
    // this happens on pre-PDP refactor products (single variant without traits)
    if (!variants.length) {
      const variant = product.variants.find((v) => v.isVisible && !v.archivedAt)

      // If no visible variant found, return empty array (product should 404 at loader level)
      if (!variant) {
        return [] as const
      }

      // Filter attachments for this specific variant
      const variantAttachments = product.attachments
        .filter(
          (attachment) =>
            !attachment.archivedAt && attachment.targetId === variant.id
        )
        .sort((a, b) => Number(a.disposition || 0) - Number(b.disposition || 0))

      return [
        {
          id: variant.id,
          name: 'Single Variant',
          attachments: variantAttachments,
          price: variant.price,
          stock: variant.stock,
          isDefault: true,
          isSingleVariant: true,
          isOutOfStock: variant.stock <= 0 && product.isStrictStock,
        },
      ] as const
    }

    return variants as [(typeof variants)[number], ...typeof variants]
  }, [product.attachments, product.isStrictStock, product.variants])
}
